import {Ranking, Wod, WodCat, WodType} from "./types";
import {computeWod, getSavedResults} from "./compute";

const computeOverall = (): [Ranking, Ranking] => {
    const savedWods = getSavedResults();
    const scaleWods: Wod[] = [];
    const rxWods: Wod[] = [];
    for (const key in savedWods) {
        const wod = savedWods[key];
        const computedWod = computeWod(wod);
        if (computedWod.category === WodCat.RX) {
            rxWods.push(computedWod);
        } else {
            scaleWods.push(computedWod);
        }
    }

    // Calculate overall rankings for Scale category
    const scaleTeamPoints = new Map<string, { team: any, totalPoints: number, overview: any[] }>();
    for (const wod of scaleWods) {
        for (const teamResult of wod.results) {
            const teamKey = `${teamResult.team.athlete1}-${teamResult.team.athlete2}`;
            if (!scaleTeamPoints.has(teamKey)) {
                scaleTeamPoints.set(teamKey, { team: teamResult.team, totalPoints: 0, overview: [] });
            }
            const teamData = scaleTeamPoints.get(teamKey)!;
            teamData.totalPoints += teamResult.points;
            teamData.overview.push({
                position: teamResult.ranking,
                points: teamResult.points
            });
        }
    }

    // Sort by total points descending and assign rankings (handle ties)
    const sortedScaleTeams = Array.from(scaleTeamPoints.values())
        .sort((a, b) => b.totalPoints - a.totalPoints);

    let currentRanking = 1;
    const scaleResults = sortedScaleTeams.map((teamData, index) => {
        // If not first team and points differ from previous, update ranking to current position
        if (index > 0 && sortedScaleTeams[index - 1].totalPoints !== teamData.totalPoints) {
            currentRanking = index + 1;
        }
        return {
            team: teamData.team,
            overview: teamData.overview,
            points: teamData.totalPoints,
            ranking: currentRanking
        };
    });

    // Calculate overall rankings for RX category
    const rxTeamPoints = new Map<string, { team: any, totalPoints: number, overview: any[] }>();
    for (const wod of rxWods) {
        for (const teamResult of wod.results) {
            const teamKey = `${teamResult.team.athlete1}-${teamResult.team.athlete2}`;
            if (!rxTeamPoints.has(teamKey)) {
                rxTeamPoints.set(teamKey, { team: teamResult.team, totalPoints: 0, overview: [] });
            }
            const teamData = rxTeamPoints.get(teamKey)!;
            teamData.totalPoints += teamResult.points;
            teamData.overview.push({
                position: teamResult.ranking,
                points: teamResult.points
            });
        }
    }

    // Sort by total points descending and assign rankings (handle ties)
    const sortedRxTeams = Array.from(rxTeamPoints.values())
        .sort((a, b) => b.totalPoints - a.totalPoints);

    currentRanking = 1;
    const rxResults = sortedRxTeams.map((teamData, index) => {
        // If not first team and points differ from previous, update ranking to current position
        if (index > 0 && sortedRxTeams[index - 1].totalPoints !== teamData.totalPoints) {
            currentRanking = index + 1;
        }
        return {
            team: teamData.team,
            overview: teamData.overview,
            points: teamData.totalPoints,
            ranking: currentRanking
        };
    });

    let scale: Ranking = {
        name: `Overall score after ${scaleWods.length} ${scaleWods.length > 1 ? "events" : "event"} (Scale)`,
        category: WodCat.SCALE,
        result: scaleResults
    };
    let rx: Ranking = {
        name: `Overall score after ${rxWods.length} ${rxWods.length > 1 ? "events" : "event"} (RX)`,
        category: WodCat.RX,
        result: rxResults
    };

    return [scale, rx];
}

const computeFinal = (): [Wod, Wod] => {
    // Get overall rankings for both categories
    const [scaleRanking, rxRanking] = computeOverall();

    // Get top 5 teams from Scale category
    const top5Scale = scaleRanking.result
        .sort((a, b) => a.ranking - b.ranking)
        .slice(0, 5);

    // Get top 5 teams from RX category
    const top5Rx = rxRanking.result
        .sort((a, b) => a.ranking - b.ranking)
        .slice(0, 5);

    // Create Scale Final WOD
    const scaleFinalWod: Wod = {
        name: "Final Event (Scale)",
        category: WodCat.SCALE,
        type: WodType.TIMECAP,
        results: top5Scale.map((teamRanking) => ({
            team: teamRanking.team,
            result: {
                mins: 0,
                seconds: 0,
                reps: 0,
                cap: false
            },
            points: teamRanking.points,
            ranking: teamRanking.ranking
        }))
    };

    // Create RX Final WOD
    const rxFinalWod: Wod = {
        name: "Final Event (Rx)",
        category: WodCat.RX,
        type: WodType.TIMECAP,
        results: top5Rx.map((teamRanking) => ({
            team: teamRanking.team,
            result: {
                mins: 0,
                seconds: 0,
                reps: 0,
                cap: false
            },
            points: teamRanking.points,
            ranking: teamRanking.ranking
        }))
    };

    return [scaleFinalWod, rxFinalWod];
}

export {computeOverall, computeFinal}